"use client"

import { useActionState, useEffect, useRef, useState } from "react"
import { REGEXP_ONLY_DIGITS } from "input-otp"

import { Button } from "@/components/ui/button"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { ErrorIcon } from "@/icons"
import { RhythmsLogo } from "@/icons/rhythms-logo"
import { EventName, track } from "@/lib/analytics"
import { type AuthState } from "@/types/auth"

interface OTPVerificationPageProps {
  email: string
  onCodeSubmit: (prevState: AuthState, formData: FormData) => Promise<AuthState>
  onBackToEmail?: () => void
  isSubmitting?: boolean
  /** Blocks submit without the verifying copy, e.g. while Teams detection is pending. */
  isSubmitDisabled?: boolean
  error?: string | null
  pendingAuthenticationToken?: string
}

const pageViewTrackingState = {
  hasTrackedPageView: false,
}

const ignoreAnalyticsError = (): undefined => undefined

export function resetOTPVerificationPageViewTrackingForTests() {
  pageViewTrackingState.hasTrackedPageView = false
}

export default function OTPVerificationPage({
  email,
  onCodeSubmit,
  onBackToEmail,
  isSubmitting = false,
  isSubmitDisabled = false,
  error,
  pendingAuthenticationToken,
}: OTPVerificationPageProps) {
  const [code, setCode] = useState("")
  const [errorDismissed, setErrorDismissed] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const [state, formAction, isPending] = useActionState(onCodeSubmit, {
    error: null,
  })
  const isVerifying = isPending || isSubmitting
  const isCodeComplete = code.length === 6
  // A code completed while submit was blocked (deep link straight to this
  // step, Teams detection still pending) auto-submits once the block lifts.
  const autoSubmitPendingRef = useRef(false)
  useEffect(() => {
    if (isSubmitDisabled || !autoSubmitPendingRef.current) return
    autoSubmitPendingRef.current = false
    formRef.current?.requestSubmit()
  }, [isSubmitDisabled])
  useEffect(() => {
    if (pageViewTrackingState.hasTrackedPageView) return
    pageViewTrackingState.hasTrackedPageView = true
    try {
      track(EventName.OTPVerificationPageViewed)
    } catch {
      // Analytics must remain best-effort; never block the auth page render.
      ignoreAnalyticsError()
    }
  }, [])

  const handleCodeChange = (value: string) => {
    setCode(value)
    if (!errorDismissed) {
      setErrorDismissed(true)
    }
  }

  const handleSubmit = () => {
    try {
      track(EventName.OTPSubmitted)
    } catch {
      // Analytics must remain best-effort; OTP submit gates authentication.
      ignoreAnalyticsError()
    }
  }

  // Auto-submit when code is complete
  const handleComplete = (value: string) => {
    setCode(value)
    setErrorDismissed(false)
    if (isVerifying) return
    if (isSubmitDisabled) {
      autoSubmitPendingRef.current = true
      return
    }
    // Use setTimeout to ensure state is updated before submit
    setTimeout(() => {
      formRef.current?.requestSubmit()
    }, 0)
  }
  const displayError = errorDismissed ? null : (state.error ?? error)
  return (
    <main className="bg-surface-secondary flex min-h-screen items-center justify-center">
      <div className="border-border bg-surface-primary flex w-full max-w-md flex-col items-center rounded-2xl border p-8 shadow-md">
        <RhythmsLogo
          width={48}
          height={48}
          className="text-brand mt-2 mb-8"
          aria-label="Rhythms logo"
        />

        <h1 className="text-content-primary mb-4 text-center text-xl font-bold">
          Enter your verification code
        </h1>

        <p className="text-content-secondary mb-8 text-center text-base leading-6">
          We sent a 6-digit code to{" "}
          <span className="text-content-primary font-semibold">{email}</span>
        </p>

        <form
          ref={formRef}
          className="flex w-full flex-col gap-6"
          action={formAction}
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="email" value={email} />
          <input type="hidden" name="code" value={code} />
          {pendingAuthenticationToken ? (
            <input
              type="hidden"
              name="pendingAuthenticationToken"
              value={pendingAuthenticationToken}
            />
          ) : null}
          <div className="w-full">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                onComplete={handleComplete}
                pattern={REGEXP_ONLY_DIGITS}
                disabled={isVerifying}
                autoFocus
                aria-label="Verification code"
              >
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot
                    index={0}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                  <InputOTPSlot
                    index={1}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                  <InputOTPSlot
                    index={2}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                  <InputOTPSlot
                    index={3}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                  <InputOTPSlot
                    index={4}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                  <InputOTPSlot
                    index={5}
                    className="border-border-strong data-[active=true]:border-brand data-[active=true]:ring-brand/30 size-12 rounded-lg border text-lg font-normal"
                  />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <Button
            type="submit"
            variant="default"
            size="xl"
            className="w-full"
            disabled={!isCodeComplete || isVerifying || isSubmitDisabled}
            isLoading={isVerifying}
            aria-label={isVerifying ? "Verifying your code" : "Verify code"}
          >
            {isVerifying ? "Verifying your code..." : "Verify"}
          </Button>
        </form>

        {displayError ? (
          <div className="border-destructive/20 bg-destructive/10 mt-4 mb-4 w-full rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <ErrorIcon
                className="text-destructive size-5 shrink-0"
                aria-hidden="true"
              />
              <p className="text-destructive text-sm">{displayError}</p>
            </div>
          </div>
        ) : null}

        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-content-tertiary text-center text-sm">
            Didn’t get it? Check your spam folder, or use a different email.
          </p>

          {onBackToEmail ? (
            <Button
              variant="link"
              onClick={onBackToEmail}
              disabled={isVerifying}
              aria-label="Use a different email"
            >
              ← Use a different email
            </Button>
          ) : null}
        </div>
      </div>
    </main>
  )
}
